/**
 * Server-safe Apollo Client utilities.
 *
 * This file contains a minimal Apollo Client factory for server-side auth
 * operations (NextAuth authorize callbacks). It does NOT import any React hooks,
 * next/router, or next-auth/react, making it safe for App Router Route Handlers.
 *
 * For full-featured Apollo Client (with auth token management, hooks, etc.),
 * use client/index.ts instead.
 */
import {
    ApolloClient,
    from,
    InMemoryCache,
    type NormalizedCacheObject,
    createHttpLink,
} from '@apollo/client';
import {setContext} from '@apollo/client/link/context';
import {
    MAGENTO_COOKIE_STORE_CODE,
    MAGENTO_STORE_CODE_KEY,
    X_API_KEY_HEADER_KEY,
} from '@/lib/constants';
import {CurrencyEnum} from '@/__generated__/__types__';
import {getValidStoreCode} from '@/_application/modules/stores/model/store-view.model';
import type {IncomingMessage, ServerResponse} from 'node:http';
import cookie from 'cookie';
import {cacheConfig} from '@/framework/client/cache.config';
import {createErrorLink} from '@kidslabs/module-core/modules/api/unified-client';

interface ApolloClientOptions {
    accessToken?: string;
    storeCode?: string;
    ignoreToken?: boolean;
}

const isClient = typeof globalThis.window !== 'undefined';

export function createApolloClient(options?: ApolloClientOptions) {
    const authLink = setContext(async (_, {headers}) => {
        const ignoreToken = options?.ignoreToken;
        const accessToken = options?.accessToken;
        const authHeader = !ignoreToken && accessToken ? {authorization: `Bearer ${accessToken}`} : {};

        const storeCode = options?.storeCode;
        const validStoreCode = getValidStoreCode(storeCode ?? '');

        const requestHeaders: Record<string, string> = {
            ...headers,
            ...authHeader,
            [MAGENTO_STORE_CODE_KEY]: validStoreCode,
            [MAGENTO_COOKIE_STORE_CODE]: validStoreCode,
        };

        requestHeaders[X_API_KEY_HEADER_KEY] = isClient ? (process.env.NEXT_PUBLIC_API_KEY ?? '') : (process.env.API_KEY_SERVER ?? '');

        return {
            headers: requestHeaders,
        };
    });

    const apiUrl = isClient ? '/api/v2/graphql' : process.env.GATEWAY_API_ENDPOINT_SERVER;

    const httpLink = createHttpLink({
        uri: apiUrl,
        credentials: isClient ? 'include' : 'same-origin',
    });

    return new ApolloClient({
        ssrMode: typeof globalThis.window === 'undefined',
        link: from([authLink, createErrorLink(), httpLink]),
        cache: new InMemoryCache(cacheConfig),
    });
}

export function initializeApolloServerSide(options?: {
    request?: IncomingMessage & {
        cookies: Partial<{
            [key: string]: string;
        }>;
    };
    response?: ServerResponse;
}) {
    if (!options || !options.request) {
        return createApolloClient();
    }
    const cookies = cookie.parse(options.request.headers?.cookie ?? '');
    const storeCode = cookies[MAGENTO_COOKIE_STORE_CODE];
    return createApolloClient({storeCode});
}

export const getGraphqlApiEndpointServerCloudEndpoint = () => {
    return process.env.GATEWAY_API_ENDPOINT_SERVER_CLOUD;
};

export const getMagentoGraphqlApiEndpointPublic = () => {
    return process.env.NEXT_PUBLIC_MAGENTO_GATEWAY_API_ENDPOINT;
};
